import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Download, Info, Loader2, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, download as dl, errMsg, inr } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { DataTable, DateRangeFilter, ExportButtons, Money, PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function monthRange(month) {
  if (!month) return { from: "", to: "" };
  return { from: `${month}-01`, to: dayjs(`${month}-01`).endOf("month").format("YYYY-MM-DD") };
}

function ReportTab({ endpoint, columns, testid, exportReport, range, extraTotals }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint, { params: { from_date: range.from, to_date: range.to } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [endpoint, range]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center flex-wrap gap-2">
        {extraTotals && data?.totals && (
          <div className="text-xs text-[#5C6B69] space-x-4" data-testid={`${testid}-totals`}>
            {Object.entries(data.totals).map(([k, v]) => (
              <span key={k}>{k.replace(/_/g, " ")}: <b className="num text-[#0D4F4A]">{inr(v)}</b></span>
            ))}
          </div>
        )}
        {exportReport && <ExportButtons base="/gst/export" params={{ report: exportReport, from_date: range.from, to_date: range.to }} testid={`${testid}-export`} />}
      </div>
      <DataTable testid={testid} columns={columns} rows={data?.rows || []} loading={loading}
        empty="No records in this period" />
    </div>
  );
}

const SALES_COLS = [
  { key: "date", label: "Date" }, { key: "invoice_no", label: "Invoice No" },
  { key: "doc_type", label: "Type" }, { key: "customer", label: "Customer" },
  { key: "gstin", label: "GSTIN", render: (r) => r.gstin || "—" },
  { key: "place_of_supply", label: "POS" },
  { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
  { key: "cgst", label: "CGST", align: "right", render: (r) => <Money value={r.cgst} /> },
  { key: "sgst", label: "SGST", align: "right", render: (r) => <Money value={r.sgst} /> },
  { key: "igst", label: "IGST", align: "right", render: (r) => <Money value={r.igst} /> },
  { key: "grand_total", label: "Total", align: "right", render: (r) => <Money value={r.grand_total} className="font-semibold" /> },
];

const PURCHASE_COLS = [
  { key: "date", label: "Date" }, { key: "vendor", label: "Vendor" },
  { key: "invoice_no", label: "Bill No" }, { key: "vendor_gstin", label: "Vendor GSTIN", render: (r) => r.vendor_gstin || "—" },
  { key: "category", label: "Category" },
  { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
  { key: "cgst", label: "CGST", align: "right", render: (r) => <Money value={r.cgst} /> },
  { key: "sgst", label: "SGST", align: "right", render: (r) => <Money value={r.sgst} /> },
  { key: "igst", label: "IGST", align: "right", render: (r) => <Money value={r.igst} /> },
  { key: "total", label: "Total", align: "right", render: (r) => <Money value={r.total} className="font-semibold" /> },
  { key: "itc_eligible", label: "ITC" },
];

function GstDashboardTab({ range, onGoIssues }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/gst/dashboard", { params: { from_date: range.from, to_date: range.to } })
      .then((r) => setD(r.data)).catch(() => {});
  }, [range]);
  if (!d) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  return (
    <div className="space-y-5" data-testid="gst-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-5 border-l-4 border-l-red-400 shadow-sm">
          <div className="text-xs font-bold uppercase tracking-wide text-[#5C6B69]">Output GST (on sales)</div>
          <div className="num text-2xl font-bold text-[#0D4F4A] mt-1" data-testid="gst-output-total">{inr(d.output.total)}</div>
          <div className="text-xs text-[#5C6B69] mt-2 num">CGST {inr(d.output.cgst)} · SGST {inr(d.output.sgst)} · IGST {inr(d.output.igst)}</div>
        </div>
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-5 border-l-4 border-l-emerald-400 shadow-sm">
          <div className="text-xs font-bold uppercase tracking-wide text-[#5C6B69]">Input GST (eligible ITC)</div>
          <div className="num text-2xl font-bold text-emerald-700 mt-1" data-testid="gst-input-total">{inr(d.input.total)}</div>
          <div className="text-xs text-[#5C6B69] mt-2 num">CGST {inr(d.input.cgst)} · SGST {inr(d.input.sgst)} · IGST {inr(d.input.igst)}</div>
        </div>
        <div className="bg-[#0D4F4A] rounded-xl p-5 shadow-md text-white border-l-4 border-l-[#C9A227]">
          <div className="text-xs font-bold uppercase tracking-wide text-[#D4AF37]">Estimated Net GST Liability</div>
          <div className="num text-2xl font-bold mt-1" data-testid="gst-net-liability">{inr(d.net_liability)}</div>
          <div className="text-xs text-white/70 mt-2">Output GST − eligible ITC</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">Taxable sales<br /><b className="num">{inr(d.taxable_sales)}</b></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">Invoices<br /><b className="num">{d.invoice_count}</b></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">Credit notes<br /><b className="num">{d.credit_note_count}</b></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">
          <button className="text-[#0D4F4A] font-semibold hover:underline" onClick={onGoIssues} data-testid="gst-review-issues-link">
            Review data issues →</button></div>
      </div>
      <div className="bg-[#C9A227]/10 border border-[#C9A227]/40 rounded-lg p-3 text-xs text-[#8a6d14]" data-testid="gst-ca-notice">
        ⚖ {d.notice}
      </div>
    </div>
  );
}

function PeriodsTab({ month, setMonth }) {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const writable = can(user?.role, "gst", true);
  const load = () => api.get("/gst/periods").then((r) => setPeriods(r.data)).catch(() => {});
  const loadChecklist = (m) => api.get(`/gst/periods/${m}/checklist`).then((r) => setChecklist(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (month) loadChecklist(month); }, [month]);

  const setStatus = async (status) => {
    try {
      await api.post(`/gst/periods/${month}/status`, { status });
      toast.success(`Period ${month} → ${status.replace("_", " ")}`);
      load();
      loadChecklist(month);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggleStep = async (step, done) => {
    try {
      await api.put(`/gst/periods/${month}/checklist`, { step, done });
      loadChecklist(month);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const current = periods.find((p) => p.month === month);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="gst-periods">
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold mb-3">Accounting Periods</h3>
        <div className="mb-3">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="period-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
            <SelectContent>
              {periods.map((p) => <SelectItem key={p.month} value={p.month}>{dayjs(p.month + "-01").format("MMMM YYYY")} — {p.status.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {current && (
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge value={current.status} testid="period-status-badge" />
            {writable && (
              <div className="flex gap-2 flex-wrap">
                {["open", "under_review", "reviewed"].map((s) => current.status !== s && (
                  <Button key={s} size="sm" variant="outline" onClick={() => setStatus(s)} data-testid={`period-set-${s}`}>
                    {s === "open" ? <LockOpen className="h-3.5 w-3.5 mr-1" /> : null}{s.replace("_", " ")}</Button>
                ))}
                {current.status !== "locked" && (
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setStatus("locked")} data-testid="period-lock-button">
                    <Lock className="h-3.5 w-3.5 mr-1" /> Lock Period</Button>
                )}
                {current.status === "locked" && (
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600" onClick={() => setStatus("open")} data-testid="period-reopen-button">
                    <LockOpen className="h-3.5 w-3.5 mr-1" /> Reopen (audited)</Button>
                )}
              </div>
            )}
          </div>
        )}
        <p className="text-xs text-[#5C6B69] mt-3">Locked periods block new/edited invoices, payments and expenses dated in that month. Reopening is audit-logged and restricted to Admin/Accountant.</p>
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold mb-3">Monthly GST Closing Checklist — {month ? dayjs(month + "-01").format("MMMM YYYY") : ""}</h3>
        {!checklist ? <p className="text-sm text-[#5C6B69]">Select a month.</p> : (
          <ul className="space-y-2.5">
            {checklist.steps.map((s, i) => (
              <li key={s.name} className="flex items-center gap-3 text-sm" data-testid={`checklist-step-${i}`}>
                <Checkbox checked={s.done} disabled={!writable || checklist.status === "locked"}
                  onCheckedChange={(v) => toggleStep(s.name, !!v)} data-testid={`checklist-step-${i}-toggle`} />
                <span className={s.done ? "line-through text-[#5C6B69]" : ""}>{i + 1}. {s.name}</span>
                {s.done && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FilingPackTab({ range }) {
  const [pack, setPack] = useState(null);
  useEffect(() => {
    api.get("/gst/filing-pack", { params: { from_date: range.from, to_date: range.to } })
      .then((r) => setPack(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [range]);
  if (!pack) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  return (
    <div className="space-y-5" data-testid="gst-filing-pack">
      <div className="bg-[#C9A227]/10 border border-[#C9A227]/40 rounded-xl p-4 text-sm text-[#8a6d14]" data-testid="filing-disclaimer">
        <b>Export for GST Filing.</b> {pack.disclaimer}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">Business<br /><b>{pack.business.legal_name}</b><br /><span className="text-xs text-[#5C6B69]">GSTIN: {pack.business.gstin || "not set"}</span></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">B2B turnover<br /><b className="num">{inr(pack.b2b.grand_total)}</b></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">B2C turnover<br /><b className="num">{inr(pack.b2c.grand_total)}</b></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">ITC available<br /><b className="num">{inr(pack.itc.itc_amount)}</b></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">GST collected<br /><b className="num">{inr(pack.summary.output.total)}</b></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">Credit notes<br /><b className="num">{inr(pack.credit_notes.grand_total)}</b></div>
        <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">HSN/SAC lines<br /><b className="num">{pack.hsn.taxable ? "Ready" : "—"}</b></div>
        <div className={`rounded-lg p-3 border ${pack.open_issues.errors ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
          Open issues<br /><b className="num">{pack.open_issues.errors} errors · {pack.open_issues.warnings} warnings</b></div>
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
        <h3 className="font-display font-semibold mb-3">Download Filing Pack (share with your CA)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {pack.exports.map((e) => (
            <div key={e.report} className="flex items-center justify-between border border-[#E5E0D8] rounded-lg px-3 py-2.5 text-sm"
              data-testid={`filing-export-${e.report}`}>
              <span className="font-medium">{e.label}</span>
              <span className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                  onClick={() => dl(`/gst/export?report=${e.report}&from_date=${range.from}&to_date=${range.to}&format=csv`)}
                  data-testid={`filing-export-${e.report}-csv`}><Download className="h-3 w-3 mr-1" />CSV</Button>
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                  onClick={() => dl(`/gst/export?report=${e.report}&from_date=${range.from}&to_date=${range.to}&format=xlsx`)}
                  data-testid={`filing-export-${e.report}-xlsx`}><Download className="h-3 w-3 mr-1" />XLSX</Button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function IssuesTab() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/gst/issues").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const icon = { error: <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />,
    warning: <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />,
    info: <Info className="h-4 w-4 text-sky-500 shrink-0" /> };
  return (
    <div data-testid="gst-issues">
      <div className="flex gap-3 mb-4 text-sm flex-wrap">
        <span className="bg-red-50 border border-red-200 text-red-700 rounded-full px-3 py-1 font-semibold" data-testid="issues-errors">{data.counts.errors} errors</span>
        <span className="bg-amber-50 border border-amber-200 text-amber-700 rounded-full px-3 py-1 font-semibold" data-testid="issues-warnings">{data.counts.warnings} warnings</span>
        <span className="bg-sky-50 border border-sky-200 text-sky-700 rounded-full px-3 py-1 font-semibold" data-testid="issues-info">{data.counts.info} info</span>
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm divide-y divide-[#E5E0D8]/60">
        {data.issues.length === 0 && <p className="p-8 text-center text-sm text-[#5C6B69]">No issues found. Data is filing-ready. ✓</p>}
        {data.issues.map((i, idx) => (
          <button key={idx} onClick={() => i.link && navigate(i.link)} data-testid={`gst-issue-${idx}`}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FAF8F5] transition-colors">
            {icon[i.severity]}
            <span className="flex-1 text-sm">{i.message}</span>
            <span className="text-[10px] font-bold uppercase tracking-wide bg-[#FAF8F5] border border-[#E5E0D8] rounded px-1.5 py-0.5 text-[#5C6B69]">{i.category}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  ["dashboard", "GST Dashboard"], ["sales", "Sales Register"], ["purchase", "Purchase Register"],
  ["b2b", "B2B"], ["b2c", "B2C"], ["credit", "Credit Notes"], ["hsn", "HSN/SAC"],
  ["itc", "ITC Register"], ["summary", "Tax Summary"], ["state", "State-wise"],
  ["customer", "Customer-wise"], ["advances", "Advances"], ["issues", "GST Issues"],
  ["closing", "Period & Closing"], ["filing", "Filing Pack"],
];

export default function GstCenter() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const mr = monthRange(month);
  const [range, setRange] = useState(mr);
  useEffect(() => setRange(monthRange(month)), [month]);

  return (
    <div data-testid="gst-center-page">
      <PageHeader title="GST Center" subtitle="Output/input GST, registers, filing preparation & period closing"
        testid="gst-center-header"
        actions={
          <div className="flex items-center gap-2">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="h-9 rounded-md border border-[#E5E0D8] bg-white px-3 text-sm" data-testid="gst-month-picker" />
            <DateRangeFilter from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} testid="gst-date-filter" />
          </div>} />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-white border border-[#E5E0D8] p-1.5 rounded-xl mb-5" data-testid="gst-tabs">
          {TABS.map(([k, l]) => (
            <TabsTrigger key={k} value={k} className="text-xs data-[state=active]:bg-[#0D4F4A] data-[state=active]:text-white"
              data-testid={`gst-tab-${k}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="dashboard"><GstDashboardTab range={range} onGoIssues={() => setParams({ tab: "issues" })} /></TabsContent>
        <TabsContent value="sales"><ReportTab endpoint="/gst/sales-register" columns={SALES_COLS} testid="gst-sales-register" exportReport="sales_register" range={range} extraTotals /></TabsContent>
        <TabsContent value="purchase"><ReportTab endpoint="/gst/purchase-register" columns={PURCHASE_COLS} testid="gst-purchase-register" exportReport="purchase_register" range={range} extraTotals /></TabsContent>
        <TabsContent value="b2b"><ReportTab endpoint="/gst/b2b" columns={SALES_COLS} testid="gst-b2b" exportReport="b2b" range={range} extraTotals /></TabsContent>
        <TabsContent value="b2c"><ReportTab endpoint="/gst/b2c" columns={SALES_COLS} testid="gst-b2c" exportReport="b2c" range={range} extraTotals /></TabsContent>
        <TabsContent value="credit"><ReportTab endpoint="/gst/credit-notes" testid="gst-credit-notes" exportReport="credit_notes" range={range} extraTotals
          columns={[...SALES_COLS.slice(0, 3), { key: "original_invoice_no", label: "Original Invoice" }, { key: "reason", label: "Reason" }, ...SALES_COLS.slice(3, 7), { key: "grand_total", label: "Credit Amt", align: "right", render: (r) => <Money value={r.grand_total} /> }]} /></TabsContent>
        <TabsContent value="hsn"><ReportTab endpoint="/gst/hsn-summary" testid="gst-hsn" exportReport="hsn_summary" range={range} extraTotals
          columns={[{ key: "sac", label: "SAC/HSN" }, { key: "gst_rate", label: "Rate", render: (r) => `${r.gst_rate}%` },
            { key: "qty", label: "Qty", align: "right" },
            { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
            { key: "cgst", label: "CGST", align: "right", render: (r) => <Money value={r.cgst} /> },
            { key: "sgst", label: "SGST", align: "right", render: (r) => <Money value={r.sgst} /> },
            { key: "igst", label: "IGST", align: "right", render: (r) => <Money value={r.igst} /> },
            { key: "total_gst", label: "Total GST", align: "right", render: (r) => <Money value={r.total_gst} className="font-semibold" /> }]} /></TabsContent>
        <TabsContent value="itc"><ReportTab endpoint="/gst/itc-register" columns={PURCHASE_COLS} testid="gst-itc" exportReport="itc_register" range={range} extraTotals /></TabsContent>
        <TabsContent value="summary"><ReportTab endpoint="/gst/summary" testid="gst-summary" exportReport="summary" range={range}
          columns={[{ key: "rate", label: "GST Rate", render: (r) => `${r.rate}%` },
            { key: "out_taxable", label: "Outward Taxable", align: "right", render: (r) => <Money value={r.out_taxable} /> },
            { key: "out_cgst", label: "CGST", align: "right", render: (r) => <Money value={r.out_cgst} /> },
            { key: "out_sgst", label: "SGST", align: "right", render: (r) => <Money value={r.out_sgst} /> },
            { key: "out_igst", label: "IGST", align: "right", render: (r) => <Money value={r.out_igst} /> },
            { key: "in_taxable", label: "Inward Taxable (ITC)", align: "right", render: (r) => <Money value={r.in_taxable} /> },
            { key: "in_cgst", label: "In CGST", align: "right", render: (r) => <Money value={r.in_cgst} /> },
            { key: "in_sgst", label: "In SGST", align: "right", render: (r) => <Money value={r.in_sgst} /> },
            { key: "in_igst", label: "In IGST", align: "right", render: (r) => <Money value={r.in_igst} /> }]} /></TabsContent>
        <TabsContent value="state"><ReportTab endpoint="/gst/state-wise" testid="gst-state-wise" exportReport="state_wise" range={range}
          columns={[{ key: "state", label: "Place of Supply" }, { key: "invoices", label: "Invoices", align: "right" },
            { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
            { key: "cgst", label: "CGST", align: "right", render: (r) => <Money value={r.cgst} /> },
            { key: "sgst", label: "SGST", align: "right", render: (r) => <Money value={r.sgst} /> },
            { key: "igst", label: "IGST", align: "right", render: (r) => <Money value={r.igst} /> }]} /></TabsContent>
        <TabsContent value="customer"><ReportTab endpoint="/gst/customer-wise" testid="gst-customer-wise" exportReport="customer_wise" range={range}
          columns={[{ key: "customer", label: "Customer" }, { key: "gstin", label: "GSTIN", render: (r) => r.gstin || "—" },
            { key: "customer_type", label: "Type" }, { key: "invoices", label: "Invoices", align: "right" },
            { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
            { key: "total_gst", label: "GST", align: "right", render: (r) => <Money value={r.total_gst} /> },
            { key: "grand_total", label: "Total", align: "right", render: (r) => <Money value={r.grand_total} className="font-semibold" /> }]} /></TabsContent>
        <TabsContent value="advances"><ReportTab endpoint="/gst/advances" testid="gst-advances" exportReport="advances" range={range} extraTotals
          columns={[{ key: "date", label: "Date" }, { key: "receipt_no", label: "Receipt" },
            { key: "customer", label: "Customer" }, { key: "mode", label: "Mode" },
            { key: "amount", label: "Advance Amount", align: "right", render: (r) => <Money value={r.amount} className="font-semibold" /> }]} /></TabsContent>
        <TabsContent value="issues"><IssuesTab /></TabsContent>
        <TabsContent value="closing"><PeriodsTab month={month} setMonth={setMonth} /></TabsContent>
        <TabsContent value="filing"><FilingPackTab range={range} /></TabsContent>
      </Tabs>
    </div>
  );
}
